import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPrint } from '@fortawesome/free-solid-svg-icons';
import cacheManager from '../CacheManager';
import { formatDateTime, formatDate, formatTime } from '../js/dateFormatter';

const PgFaturas = () => {
  
  const [clientesByIdCache, setClientesByIdCache] = useState({});
  const [itensByIdCache, setItensByIdCache] = useState({});
  
  const [Faturas, setFaturas] = useState([]);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const FaturasPorPagina = 10;
  const [termoDeBusca, setTermoDeBusca] = useState('');
  const [FaturasFiltrados, setFaturasFiltrados] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const search = async () => {
    try {
      setIsLoading(true);

      const clientesByIdCache = await cacheManager.getClientesById()
      setClientesByIdCache(clientesByIdCache);
      const itensByIdCache = await cacheManager.getItensById()
      setItensByIdCache(itensByIdCache);
      const FaturasCache = await cacheManager.getFaturasCache();
      setFaturas(FaturasCache);

    } catch (error) {
      console.error('Erro ao buscar Faturas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Estilos para esmaecer a página
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 2, // Garanta que esteja acima de outros elementos da página
    display: isLoading ? 'block' : 'none', // Mostra apenas quando está carregando
  };

  useEffect(() => {
    search(); // Chama a função search ao montar o componente
  }, []); // O array vazio como segundo argumento garante que o efeito seja executado apenas uma vez, na montagem do componente

  useEffect(() => {
    const FaturasOrdenados = [...Faturas].sort((a, b) => {
      const idA = a.id_fatura;
      const idB = b.id_fatura;

      return idB - idA;
    });

    const FaturasFiltrados = FaturasOrdenados.filter(fatura => {
      let nomeCliente = clientesByIdCache[fatura.id_cliente]?.nome || ''
      nomeCliente = nomeCliente.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
      return nomeCliente.includes(termoDeBusca.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase());
    });

    setFaturasFiltrados(FaturasFiltrados);
    setPaginaAtual(1);
  }, [Faturas, termoDeBusca]);

  const addFatura = () => {
    navigate('/fatura/novo');
  };

  const handleImprimirRecibo = async (fatura) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('Token não encontrado! Faça login novamente.');
        return;
      }
      
      const url = `https://api.faeworks.com.br/imprimir/recibo.php?id=${fatura.id_fatura}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Bearer ${token}`,
          'X-Auth-Token': token, // Header alternativo caso Authorization seja bloqueado
        },
      });
  
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `recibo_${fatura.id_fatura}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Erro ao imprimir fatura externa:', error);
    }
  };

  const handleImprimirFaturaExterna = async (fatura) => {
    try {
      const formData = new URLSearchParams();
      for (const key in fatura) {
        if (fatura.hasOwnProperty(key)) {
          formData.append(key, fatura[key]);
        }
      }
  
      const response = await fetch('https://api.faeworks.com.br/imprimir/fatura_externa.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });
  
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fatura_${fatura.fat_numero_da_fatura}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Erro ao imprimir fatura externa:', error);
    }
  };

  

  // Calcular o número total de páginas
  const totalPaginas = Math.ceil(FaturasFiltrados.length / FaturasPorPagina);

  // Obter os Faturas da página atual
  const indexDoUltimoAgendamento = paginaAtual * FaturasPorPagina;
  const indexDoPrimeiroAgendamento = indexDoUltimoAgendamento - FaturasPorPagina;
  const FaturasAtuais = FaturasFiltrados.slice(indexDoPrimeiroAgendamento, indexDoUltimoAgendamento);

  // Funções para alterar a página
  const irParaPagina = (numero) => {
    setPaginaAtual(numero);
  };

  return (
    <div className="container mt-5">
      {isLoading && <div style={overlayStyle}></div>}
      <h2 className="mb-4">Faturas</h2>
      <div className="input-group mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Buscar..."
          id="search-input"
          value={termoDeBusca}
          onChange={(e) => setTermoDeBusca(e.target.value)}
        />
        <button className="btn btn-outline-secondary" type="button" onClick={search}>Buscar</button>
      </div>
      <table className="table">
        <thead className="table-light">
          <tr>
            <th>Nº</th>
            <th>Cliente</th>
            <th>Data</th>
            <th>Valor</th>
            <th>Situação</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {FaturasAtuais.map((fatura, index) => (
            <tr key={index} style={{ height: 'auto !important', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <td>
                <Link to={`/fatura/editar/${fatura.id_fatura}`} className="text-dark underline-on-hover">
                  {fatura.id_fatura}
                  {(fatura.fat_numero_da_fatura && (String(fatura.fat_numero_da_fatura).trim() !== '' && fatura.fat_numero_da_fatura !== "0") && fatura.fat_numero_da_fatura !== "0") && ` (${fatura.fat_numero_da_fatura})`}
                </Link>
              </td>
              <td>{clientesByIdCache[fatura.id_cliente]?.nome || 'Cliente não encontrado'}</td>
              <td>{formatDate(fatura.data_emissao)}</td>
              <td>R$ {fatura.valor_total}</td>
              <td>{fatura.situacao}</td>
              <td>
                <Link to={`/fatura/editar/${fatura.id_fatura}`} className="btn btn-primary btn-sm me-2">
                  <FontAwesomeIcon icon={faEdit} /> Editar
                </Link>
                <button className="btn btn-danger btn-sm" disabled>
                  <FontAwesomeIcon icon={faTrash} /> Excluir
                </button>
                &nbsp;
                <div className="dropdown d-inline">
                  <button className="btn btn-secondary btn-sm dropdown-toggle" type="button" id="dropdownMenuButton" data-bs-toggle="dropdown" aria-expanded="false">
                    <FontAwesomeIcon icon={faPrint} /> Imprimir
                  </button>
                  <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                    <li><button className="dropdown-item" onClick={() => handleImprimirRecibo(fatura)}>Recibo</button></li>
                    {fatura.fat_numero_da_fatura && (
                      <li><button className="dropdown-item" onClick={() => handleImprimirFaturaExterna(fatura)}>Fatura Externa</button></li>
                    )}
                  </ul>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="d-flex justify-content-between align-items-center">
        <div>
          {/* Conteúdo da paginação */}
          <button onClick={() => irParaPagina(1)} disabled={paginaAtual === 1} className="btn btn-secondary" style={{ fontSize: '10px' }}>
            Primeira
          </button>
          {paginaAtual > 2 && (
            <button onClick={() => irParaPagina(paginaAtual - 2)} className="btn btn-secondary" style={{ fontSize: '10px' }}>
              {paginaAtual - 2}
            </button>
          )}
          {paginaAtual > 1 && (
            <button onClick={() => irParaPagina(paginaAtual - 1)} className="btn btn-secondary" style={{ fontSize: '10px' }}>
              {paginaAtual - 1}
            </button>
          )}
          <button className="btn btn-primary" style={{ fontSize: '10px' }}>
            {paginaAtual}
          </button>
          {paginaAtual < totalPaginas && (
            <button onClick={() => irParaPagina(paginaAtual + 1)} className="btn btn-secondary" style={{ fontSize: '10px' }}>
              {paginaAtual + 1}
            </button>
          )}
          {paginaAtual < totalPaginas - 1 && (
            <button onClick={() => irParaPagina(paginaAtual + 2)} className="btn btn-secondary" style={{ fontSize: '10px' }}>
              {paginaAtual + 2}
            </button>
          )}
          <button onClick={() => irParaPagina(totalPaginas)} disabled={paginaAtual === totalPaginas} className="btn btn-secondary" style={{ fontSize: '10px' }}>
            Última
          </button>
        </div>
        <div>
          <button type="button" className="btn btn-primary" onClick={addFatura}>Incluir</button>
        </div>
      </div>
    </div>
  );
};

export default PgFaturas;